import React, { Fragment, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'formik';
import iso3311a2 from 'iso-3166-1-alpha-2';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import Document from 'components/Document/Document';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import ClockCircleFilled from '@ant-design/icons/ClockCircleFilled';
import {
  AntInput,
  AntSelect,
  AntAutoComplete
} from 'components/UI/Antd/AntdInputWithFormik';
import AntdSwitchWithFormik from 'components/UI/Antd/AntdSwitchWithFormik';
import AntdDatePickerWithFormik from 'components/UI/Antd/AntdDatePickerWithFormik';
import Row from 'components/UI/Antd/Grid/Row';
import Col from 'components/UI/Antd/Grid/Col';
import Button from 'components/UI/Antd/Button/Button';
import configForFE from 'common-pickapier/configForFE';
import { groups } from './Submission';
import { isUnverifiedMarina } from 'library/helpers/utils';
import {
  SubmissionGroup,
  GroupTitle,
  GroupBottomTitle,
  GroupBottomSubtitle,
  RowWrapper,
  FormFieldWithStatus,
  MarinaStatus,
  NotFound,
  BottomSectionActions,
  DocumentList,
  ErrorMessage
} from './Submission.style';

import { NatureOfTravel } from 'settings/enums';

const AsyncButton = ButtonWithAsync(Button);
const errorIndication = { className: 'has-error' }; // For scrolling to error functionality.

const labels = {
  first_name: 'FIRST_NAME',
  last_name: 'LAST_NAME',
  email_address: 'EMAIL',
  date_of_birth: 'DATE_OF_BIRTH',
  country_of_residence: 'COUNTRY',
  phone: 'PHONE_NUMBER',
  address: 'ADDRESS_LABEL',
  address_line_two: 'ADDRESS_LINE_TWO_LABEL',
  postal_code: 'ZIPCODE_LABEL',
  vessel_name: 'VESSEL_NAME_LABEL',
  vessel_type: 'VESSEL_TYPE_LABEL',
  vessel_hull_type: 'MONO_MULTI_HULL',
  vessel_beam: 'VESSEL_BEAM_LABEL',
  vessel_draft: 'VESSEL_DRAFT_LABEL',
  vessel_loa: 'VESSEL_LOA_LABEL',
  home_marina_name: 'HOME_MARINA_LABEL',
  water_electricity: 'ELECTRICITY_WATER_LABEL',
  vessel_insurance: 'VESSEL_INSURANCE',
  vessel_license: 'VESSEL_LICENSE',
  skipper_id: 'SKIPPER_ID_LICENSE',
  private_or_commercial:'BOAT_USAGE',
  live_aboard:"Liveaboard (live onboard)"
};

export default function RenderSubmissionForm({
  submitForm,
  handleReset,
  values,
  submitCount,
  setFieldValue,
  errors,

  isFilledSection,
  marinaName,
  refreshData,
  onChangeAttachment = () => {},

  personalGroup,
  vesselGroup,
  documentsGroup,
  tripInfo,

  home_marina_id,
  home_marina_verified,
  home_marina_name,
  inputRef,
  onSelect,
  onChange,
  otherSelected,
  options,
  defaultOpen,
  isCanceled,
  isApproved,
  editMode,
  onEdit,
  onCancel,
  onClickFilledSubmit,
  vessel_id,
  onAddNewHomeMarina
}) {
  const { t } = useTranslation();

  const [newHomeMarina, setNewHomeMarina] = useState(null);
  const pressPhoneNumberKeyCondition = code =>
    (code < 48 || code > 57) && code !== 8 && code !== 43 && code !== 45;
  const handlePhoneNumberKeyPress = e => {
    if (pressPhoneNumberKeyCondition(e.charCode)) {
      e.preventDefault();
    }
  };
  const getStatus = () => {
    let icon = <CheckCircleFilled style={{ fontSize: 14, color: '#50bd43' }} />;
    let title = 'MARINA_VERIFIED_STATUS';
    if (!home_marina_verified) {
      if (home_marina_id) {
        title = 'MARINA_WAITING_VERIFICATION_STATUS';
        icon = <ClockCircleFilled style={{ fontSize: 14, color: '#bfbfbf' }} />;
      } else {
        title = 'MARINA_NOT_VERIFIED_STATUS';
        icon = null;
      }
    }
    return (
      <Fragment>
        {icon ? icon : null}
        {t(title)}
      </Fragment>
    );
  };
  const documentsError =
    documentsGroup.length &&
    Object.keys(errors).filter(key =>
      documentsGroup.some(docKey => docKey === key)
    ).length;

  return (
    <Form>
      {!!personalGroup.length && (
        <SubmissionGroup className={isFilledSection ? 'is-filled-form' : ''}>
          {isFilledSection ? (
            <Fragment>
              <section className={isFilledSection && 'title-and-buttons'}>
                <GroupBottomTitle>
                  {t(groups.personal_info.title)}
                </GroupBottomTitle>
                {isFilledSection && !isApproved && (
                  <BottomSectionActions>
                    {!editMode && (
                      <Button
                        type='link'
                        htmlType='button'
                        disabled={isCanceled}
                        onClick={onEdit}
                      >
                        {t('EDIT')}
                      </Button>
                    )}
                    {editMode && (
                      <Fragment>
                        <AsyncButton
                          asyncAction={submitForm}
                          onMouseDown={onClickFilledSubmit}
                          htmlType='button'
                          type='link'
                          label={t('SAVE')}
                        />
                        <Button
                          type='link'
                          htmlType='button'
                          onClick={() => {
                            handleReset();
                            onCancel();
                          }}
                        >
                          {t('CANCEL')}
                        </Button>
                      </Fragment>
                    )}
                  </BottomSectionActions>
                )}
              </section>
              <GroupBottomSubtitle>
                {t('SHARED_BY_DEFAULT_WITH_RESERVATIONS')}
              </GroupBottomSubtitle>
            </Fragment>
          ) : (
            <GroupTitle>{t(groups.personal_info.title)}</GroupTitle>
          )}
          <RowWrapper className={isFilledSection ? 'filled-row' : ''}>
            <Row gutter={30}>
              {personalGroup.map((field, key) => {
                switch (field) {
                  case 'date_of_birth':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntdDatePickerWithFormik}
                          format='DD/MM/YYYY'
                          name='date_of_birth'
                          label={t(labels[field])}
                          defaultValue={values.date_of_birth}
                          value={values.date_of_birth}
                          submitCount={submitCount}
                          inputReadOnly={isMobile}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                  case 'country_of_residence':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntSelect}
                          name={field}
                          label={t(labels[field])}
                          placeholder={t('COUNTRY_PLACEHOLDER')}
                          showSearch
                          defaultValue={values.country_of_residence}
                          selectOptions={iso3311a2.getData()}
                          submitCount={submitCount}
                          filterOption={(input, option) =>
                            option.props.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          tokenSeparators={[',']}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                  case 'phone':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntInput}
                          name={field}
                          type='text'
                          label={t(labels[field])}
                          submitCount={submitCount}
                          onKeyPress={e => handlePhoneNumberKeyPress(e)}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                  case 'email_address':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          component={AntInput}
                          name={field}
                          type='text'
                          label={t(labels[field])}
                          submitCount={submitCount}
                          disabled
                          {...errorIndication}
                        />
                      </Col>
                    );
                  default:
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntInput}
                          name={field}
                          type='text'
                          label={t(labels[field])}
                          submitCount={submitCount}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                }
              })}
            </Row>
          </RowWrapper>
        </SubmissionGroup>
      )}
      {!!vesselGroup.length && (
        <SubmissionGroup className={isFilledSection ? 'is-filled-form' : ''}>
          {isFilledSection ? (
            <Fragment>
              <GroupBottomTitle>{t(groups.vessel_info.title)}</GroupBottomTitle>
              <GroupBottomSubtitle>
                {t('SHARED_BY_DEFAULT_WITH_RESERVATIONS')}
              </GroupBottomSubtitle>
            </Fragment>
          ) : (
            <GroupTitle>{t(groups.vessel_info.title)}</GroupTitle>
          )}
          <RowWrapper className={isFilledSection ? 'filled-row' : ''}>
            <Row gutter={30}>
              {vesselGroup.map((field, key) => {
                switch (field) {
                  case 'home_marina_name':
                    return (
                      <Fragment key={key}>
                        <Col xs={24} lg={12} key={key}>
                          <FormFieldWithStatus>
                            <Field
                              innerRef={inputRef}
                              defaultOpen={defaultOpen}
                              notFoundContent={
                                <NotFound>
                                  <span>
                                    {t(
                                      'NO_RESULTS_FOUND_SEARCH_AGAIN_OR_SELECT'
                                    )}
                                  </span>
                                  <Button
                                    htmlType='button'
                                    type='link'
                                    onClick={() =>
                                      onAddNewHomeMarina(newHomeMarina)
                                    }
                                  >
                                    {t('ADD')}
                                  </Button>
                                </NotFound>
                              }
                              disabled={Boolean(
                                home_marina_verified ||
                                  home_marina_id ||
                                  home_marina_name ||
                                  !editMode
                              )}
                              component={AntAutoComplete}
                              name='home_marina_id'
                              label={
                                <div className='label-with-status'>
                                  <span>{t('HOME_MARINA_LABEL')}</span>
                                  <MarinaStatus>
                                    {(!otherSelected ||
                                      home_marina_id ||
                                      home_marina_verified) && (
                                      <Fragment>|&nbsp;&nbsp;</Fragment>
                                    )}
                                    {getStatus()}
                                  </MarinaStatus>
                                </div>
                              }
                              placeholder={t('SELECT_MARINA')}
                              value={
                                values.home_marina_id
                                  ? isUnverifiedMarina(values.home_marina_id)
                                    ? `${values.home_marina_id}`.split('_')[1]
                                    : options[values.home_marina_id]
                                  : null
                              }
                              selectOptions={options}
                              filterOption={(inputValue, option) =>
                                option.children
                                  .toUpperCase()
                                  .indexOf(inputValue.toUpperCase()) !== -1
                              }
                              dropdownAlign={{
                                points: ['bl', 'tl'],
                                offset: [0, -4],
                                overflow: {
                                  adjustX: 0,
                                  adjustY: 0
                                }
                              }}
                              onSelect={onSelect}
                              onChange={value => {
                                setNewHomeMarina(value);
                                onChange(value);
                              }}
                              submitCount={submitCount}
                              {...errorIndication}
                            />
                          </FormFieldWithStatus>
                        </Col>
                      </Fragment>
                    );
                  case 'water_electricity':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntdSwitchWithFormik}
                          name='water_electricity'
                          type='text'
                          label={t(labels[field])}
                          {...errorIndication}
                        />
                      </Col>
                    );
                  case 'vessel_type':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntSelect}
                          name='vessel_type'
                          label={t(labels[field])}
                          defaultValue={values.vessel_type}
                          value={values.vessel_type}
                          selectOptions={configForFE.VESSEL_TYPES.reduce(
                            (map, type) => ({ ...map, [type]: t(type) }),
                            {}
                          )}
                          submitCount={submitCount}
                          tokenSeparators={[',']}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                  case 'vessel_hull_type':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntSelect}
                          name='vessel_hull_type'
                          label={t(labels[field])}
                          defaultValue={values.vessel_hull_type}
                          value={values.vessel_hull_type}
                          selectOptions={configForFE.VESSEL_HULL_TYPES.reduce(
                            (map, type) => ({ ...map, [type]: t(type) }),
                            {}
                          )}
                          submitCount={submitCount}
                          tokenSeparators={[',']}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                  default:
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntInput}
                          name={field}
                          type='text'
                          label={t(labels[field])}
                          submitCount={submitCount}
                          {...errorIndication}
                        />
                      </Col>
                    );
                }
              })}
            </Row>
          </RowWrapper>
        </SubmissionGroup>
      )}
      {!!documentsGroup.length && (
        <SubmissionGroup className={isFilledSection ? 'is-filled-form' : ''}>
          {isFilledSection ? (
            <Fragment>
              <GroupBottomTitle>{t(groups.documents.title)}</GroupBottomTitle>
              <GroupBottomSubtitle>
                {t('SHARED_BY_DEFAULT_WITH_RESERVATIONS')}
              </GroupBottomSubtitle>
            </Fragment>
          ) : (
            <GroupTitle>{t(groups.documents.title)}</GroupTitle>
          )}
          <RowWrapper className={isFilledSection ? 'filled-row' : ''}>
            <DocumentList className={documentsError ? 'has-error' : ''}>
              {documentsGroup.some(docKey => docKey === 'vessel_license') && (
                <Document
                  label={t('VESSEL_LICENSE')}
                  type='VESSEL_LICENSE'
                  name='vessel_license'
                  file={values.vessel_license}
                  onChange={value => {
                    if (isFilledSection) {
                      onChangeAttachment(value);
                    } else {
                      refreshData({ parameters: ['vessels'], vessel_id });
                    }
                  }}
                  marinaName={marinaName}
                  actionsAvailable={!isFilledSection}
                  disabled={!editMode}
                  vessel_id={vessel_id}
                />
              )}
              {documentsGroup.some(docKey => docKey === 'vessel_insurance') && (
                <Document
                  label={t('VESSEL_INSURANCE')}
                  type='VESSEL_INSURANCE'
                  name='vessel_insurance'
                  file={values.vessel_insurance}
                  onChange={value => {
                    if (isFilledSection) {
                      onChangeAttachment(value);
                    } else {
                      refreshData({ parameters: ['vessels'], vessel_id });
                    }
                  }}
                  marinaName={marinaName}
                  actionsAvailable={!isFilledSection}
                  disabled={!editMode}
                  vessel_id={vessel_id}
                />
              )}
              {documentsGroup.some(docKey => docKey === 'skipper_id') && (
                <Document
                  label={t('SKIPPER_ID_LICENSE')}
                  type='SKIPPER_ID_LICENSE'
                  name='skipper_id'
                  file={values.skipper_id}
                  onChange={value => {
                    if (isFilledSection) {
                      onChangeAttachment(value);
                    } else {
                      refreshData({ parameters: ['attachments'] });
                    }
                  }}
                  marinaName={marinaName}
                  actionsAvailable={!isFilledSection}
                  disabled={!editMode}
                />
              )}
              {documentsGroup.some(docKey => docKey === 'vessel_photo') && (
                <Document
                  label={t('VESSEL_PHOTO')}
                  type='VESSEL_PHOTO'
                  name='vessel_photo'
                  file={values.vessel_photo}
                  onChange={value => {
                    if (isFilledSection) {
                      onChangeAttachment(value);
                    } else {
                      refreshData({ parameters: ['vessels'], vessel_id });
                    }
                  }}
                  marinaName={marinaName}
                  actionsAvailable={!isFilledSection}
                  disabled={!editMode}
                  vessel_id={vessel_id}
                />
              )}
            </DocumentList>
            {submitCount > 0 && !!documentsError && (
              <ErrorMessage>{t('REQUIRED_DOCUMENTS_ERROR')}</ErrorMessage>
            )}
          </RowWrapper>
        </SubmissionGroup>
      )}
      {!!tripInfo.length && (
        <SubmissionGroup className={isFilledSection ? 'is-filled-form' : ''}>
          {isFilledSection ? (
            <Fragment>
              <GroupBottomTitle>{t(groups.trip_info.title)}</GroupBottomTitle>
              <GroupBottomSubtitle>
                {t('SHARED_BY_DEFAULT_WITH_RESERVATIONS')}
              </GroupBottomSubtitle>
            </Fragment>
          ) : (
            <GroupTitle>{t(groups.trip_info.title)}</GroupTitle>
          )}
          <RowWrapper className={isFilledSection ? 'filled-row' : ''}>
            <Row gutter={30}>
              {tripInfo.map((field, key) => {
                switch (field) {
                  case 'live_aboard':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntdSwitchWithFormik}
                          name='live_aboard'
                          type='text'
                          label={t(labels[field])}
                          {...errorIndication}
                        />
                      </Col>
                    );
                  case 'private_or_commercial':
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntSelect}
                          name='private_or_commercial'
                          label={t(labels[field])}
                          defaultValue={values.private_or_commercial}
                          value={values.private_or_commercial}
                          selectOptions={Object.keys(NatureOfTravel).map(opt => ({label: t(opt), value: opt}))}
                          submitCount={submitCount}
                          tokenSeparators={[',']}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntSelect}
                          name='vessel_hull_type'
                          label={t(labels[field])}
                          defaultValue={values.vessel_hull_type}
                          value={values.vessel_hull_type}
                          selectOptions={configForFE.VESSEL_HULL_TYPES.reduce(
                            (map, type) => ({ ...map, [type]: t(type) }),
                            {}
                          )}
                          submitCount={submitCount}
                          tokenSeparators={[',']}
                          hasFeedback
                          {...errorIndication}
                        />
                      </Col>
                    );
                  default:
                    return (
                      <Col xs={24} lg={12} key={key}>
                        <Field
                          disabled={!editMode}
                          component={AntInput}
                          name={field}
                          type='text'
                          label={t(labels[field])}
                          submitCount={submitCount}
                          {...errorIndication}
                        />
                      </Col>
                    );
                }
              })}
            </Row>
          </RowWrapper>
        </SubmissionGroup>
      )}
    </Form>
  );
}
